import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot, UserRound, ChevronRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "nexus_chat_session_v1";
const HISTORY_KEY = "nexus_chat_history_v1";

const STARTER_KEYS = ["bulkPricing", "leadTimes", "coas"];

let msgIdCounter = 0;
const nextMsgId = () => `m-${Date.now()}-${msgIdCounter++}`;

export default function ChatWidget() {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [initialised, setInitialised] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
    const [messages, setMessages] = useState(() => {
        try {
            const parsed = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
            return parsed.map((m) => (m.id ? m : { ...m, id: nextMsgId() }));
        } catch (err) {
            console.error("[Chat] failed to parse stored history:", err);
            return [];
        }
    });
    const [draft, setDraft] = useState("");
    const [streaming, setStreaming] = useState(false);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    // Persist history
    useEffect(() => {
        try {
            localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40)));
        } catch (err) {
            console.error("[Chat] failed to persist history:", err);
        }
    }, [messages]);

    // Initialise a session the first time the widget opens
    useEffect(() => {
        if (!open || initialised) return;
        setInitialised(true);
        if (!sessionId) {
            fetch(`${BACKEND_URL}/api/chat/init?lang=${i18n.language || "en"}`)
                .then((r) => r.json())
                .then((data) => {
                    setSessionId(data.session_id);
                    localStorage.setItem(STORAGE_KEY, data.session_id);
                    if (messages.length === 0) {
                        setMessages([{ id: nextMsgId(), role: "assistant", content: data.greeting }]);
                    }
                })
                .catch(() => {
                    if (messages.length === 0) {
                        setMessages([{ id: nextMsgId(), role: "assistant", content: t("chat.fallbackGreeting") }]);
                    }
                });
        } else if (messages.length === 0) {
            setMessages([{ id: nextMsgId(), role: "assistant", content: t("chat.fallbackGreeting") }]);
        }
        setTimeout(() => inputRef.current?.focus(), 100);
    }, [open, initialised, sessionId, i18n.language, messages.length, t]);

    // Auto-scroll on new messages / streaming
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, streaming]);

    const send = async () => {
        const text = draft.trim();
        if (!text || streaming) return;
        setDraft("");
        setMessages((prev) => [
            ...prev,
            { id: nextMsgId(), role: "user", content: text },
            { id: nextMsgId(), role: "assistant", content: "", streaming: true },
        ]);
        setStreaming(true);
        try {
            const res = await fetch(`${BACKEND_URL}/api/chat/send`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, message: text }),
            });
            if (!res.ok || !res.body) {
                if (res.status === 429) {
                    let msg = t("chat.rateLimited");
                    try {
                        const j = await res.json();
                        if (j?.detail) msg = j.detail;
                    } catch (err) {
                        console.error("[Chat] failed to parse rate-limit response:", err);
                    }
                    setMessages((prev) => {
                        const next = [...prev];
                        next[next.length - 1] = { ...next[next.length - 1], content: msg, error: true, streaming: false };
                        return next;
                    });
                    setStreaming(false);
                    return;
                }
                throw new Error("chat unavailable");
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let carry = "";
            let acc = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                carry += decoder.decode(value, { stream: true });
                const parts = carry.split("\n\n");
                carry = parts.pop() || "";
                for (const chunk of parts) {
                    const line = chunk.replace(/^data:\s*/, "").trim();
                    if (!line) continue;
                    try {
                        const payload = JSON.parse(line);
                        if (payload.delta) {
                            acc += payload.delta;
                            setMessages((prev) => {
                                const next = [...prev];
                                next[next.length - 1] = { ...next[next.length - 1], content: acc, streaming: true };
                                return next;
                            });
                        }
                        if (payload.done) {
                            if (payload.session_id && payload.session_id !== sessionId) {
                                setSessionId(payload.session_id);
                                localStorage.setItem(STORAGE_KEY, payload.session_id);
                            }
                            setMessages((prev) => {
                                const next = [...prev];
                                next[next.length - 1] = { ...next[next.length - 1], content: acc, streaming: false };
                                return next;
                            });
                        }
                        if (payload.error) {
                            setMessages((prev) => {
                                const next = [...prev];
                                next[next.length - 1] = { ...next[next.length - 1], content: payload.error, error: true, streaming: false };
                                return next;
                            });
                        }
                    } catch (err) {
                        console.error("[Chat] failed to parse stream chunk:", err);
                    }
                }
            }
        } catch (err) {
            console.error("[Chat] send failed:", err);
            setMessages((prev) => {
                const next = [...prev];
                next[next.length - 1] = { ...next[next.length - 1], content: t("chat.errorGeneric"), error: true, streaming: false };
                return next;
            });
        } finally {
            setStreaming(false);
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const sendPrompt = (text) => {
        if (streaming) return;
        setDraft(text);
        // Give React a tick to set draft then flush
        setTimeout(() => {
            setDraft("");
            setMessages((prev) => [
                ...prev,
                { id: nextMsgId(), role: "user", content: text },
                { id: nextMsgId(), role: "assistant", content: "", streaming: true },
            ]);
            setStreaming(true);
            // Fire the same streaming logic — reuse send() by populating draft is racy, so inline instead:
            (async () => {
                try {
                    const res = await fetch(`${BACKEND_URL}/api/chat/send`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ session_id: sessionId, message: text }),
                    });
                    if (!res.ok || !res.body) {
                        if (res.status === 429) {
                            let msg = t("chat.rateLimited");
                            try {
                                const j = await res.json();
                                if (j?.detail) msg = j.detail;
                            } catch (err) {
                                console.error("[Chat] failed to parse rate-limit response:", err);
                            }
                            setMessages((prev) => {
                                const next = [...prev];
                                next[next.length - 1] = { ...next[next.length - 1], content: msg, error: true, streaming: false };
                                return next;
                            });
                            setStreaming(false);
                            return;
                        }
                        throw new Error();
                    }
                    const reader = res.body.getReader();
                    const decoder = new TextDecoder();
                    let carry = "";
                    let acc = "";
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;
                        carry += decoder.decode(value, { stream: true });
                        const parts = carry.split("\n\n");
                        carry = parts.pop() || "";
                        for (const chunk of parts) {
                            const line = chunk.replace(/^data:\s*/, "").trim();
                            if (!line) continue;
                            try {
                                const payload = JSON.parse(line);
                                if (payload.delta) {
                                    acc += payload.delta;
                                    setMessages((prev) => {
                                        const next = [...prev];
                                        next[next.length - 1] = { ...next[next.length - 1], content: acc, streaming: true };
                                        return next;
                                    });
                                }
                                if (payload.done) {
                                    if (payload.session_id && payload.session_id !== sessionId) {
                                        setSessionId(payload.session_id);
                                        localStorage.setItem(STORAGE_KEY, payload.session_id);
                                    }
                                    setMessages((prev) => {
                                        const next = [...prev];
                                        next[next.length - 1] = { ...next[next.length - 1], content: acc, streaming: false };
                                        return next;
                                    });
                                }
                            } catch (err) {
                                console.error("[Chat] failed to parse stream chunk:", err);
                            }
                        }
                    }
                } catch (err) {
                    console.error("[Chat] send prompt failed:", err);
                    setMessages((prev) => {
                        const next = [...prev];
                        next[next.length - 1] = { ...next[next.length - 1], content: t("chat.errorGeneric"), error: true, streaming: false };
                        return next;
                    });
                } finally {
                    setStreaming(false);
                }
            })();
        }, 20);
    };

    // Handoff state
    const [handoffView, setHandoffView] = useState(false);
    const [handoff, setHandoff] = useState({ email: "", name: "", note: "", loading: false, done: false, error: "" });

    const submitHandoff = async (e) => {
        e.preventDefault();
        if (!sessionId) return;
        setHandoff((s) => ({ ...s, loading: true, error: "" }));
        try {
            const res = await fetch(`${BACKEND_URL}/api/chat/handoff`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    session_id: sessionId,
                    email: handoff.email,
                    name: handoff.name || null,
                    note: handoff.note || null,
                }),
            });
            if (!res.ok) throw new Error();
            setHandoff((s) => ({ ...s, loading: false, done: true }));
        } catch {
            setHandoff((s) => ({ ...s, loading: false, error: t("chat.handoffError") }));
        }
    };

    const userMsgCount = messages.filter((m) => m.role === "user").length;
    const showStarters = userMsgCount === 0 && !handoffView;

    return (
        <>
            {/* Floating button */}
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={t("chat.toggle")}
                className={`fixed z-40 bottom-5 right-5 sm:bottom-6 sm:right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(0,137,200,0.35)] transition-all ${
                    open ? "bg-neutral-900 hover:bg-black" : "bg-[#0089c8] hover:bg-[#0077b6]"
                } text-white`}
                data-testid="chat-toggle"
            >
                {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-6 h-6" />}
            </button>

            {/* Panel */}
            {open && (
                <div
                    className="fixed z-40 bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[380px] max-w-[420px] h-[540px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl border border-neutral-200 shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden"
                    data-testid="chat-panel"
                >
                    {/* Header */}
                    <div className="px-5 py-4 border-b border-neutral-200 bg-gradient-to-br from-white to-[#00aeef]/[0.08]">
                        <div className="flex items-center gap-3">
                            {handoffView && (
                                <button
                                    type="button"
                                    onClick={() => setHandoffView(false)}
                                    className="w-8 h-8 rounded-lg border border-neutral-200 hover:border-[#0089c8] flex items-center justify-center text-neutral-600 flex-shrink-0"
                                    aria-label={t("chat.back")}
                                    data-testid="chat-handoff-back"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                </button>
                            )}
                            <div className="w-9 h-9 rounded-full bg-[#0089c8] text-white flex items-center justify-center flex-shrink-0">
                                {handoffView ? <UserRound className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display font-bold text-neutral-900 text-sm">
                                    {handoffView ? t("chat.handoffTitle") : t("chat.title")}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] flex items-center gap-1.5">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                    {handoffView ? t("chat.handoffSubtitle") : t("chat.online")}
                                </div>
                            </div>
                            {!handoffView && (
                                <button
                                    type="button"
                                    onClick={() => setHandoffView(true)}
                                    className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0077b6] hover:text-[#005f92] border border-[#00aeef]/40 hover:border-[#00aeef] bg-white/70 hover:bg-[#00aeef]/[0.1] rounded-lg px-2.5 py-1.5 flex items-center gap-1 transition-colors"
                                    data-testid="chat-handoff-btn"
                                >
                                    <UserRound className="w-3 h-3" />
                                    {t("chat.askHuman")}
                                </button>
                            )}
                        </div>
                    </div>

                    {handoffView ? (
                        <div className="flex-1 overflow-y-auto px-5 py-5" data-testid="chat-handoff-form">
                            {handoff.done ? (
                                <div className="text-center py-6" data-testid="chat-handoff-done">
                                    <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
                                    <div className="font-display font-bold text-neutral-900">
                                        {t("chat.handoffDone")}
                                    </div>
                                    <div className="text-xs text-neutral-500 mt-1">
                                        {t("chat.handoffDoneBody")}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setHandoffView(false);
                                            setHandoff({ email: "", name: "", note: "", loading: false, done: false, error: "" });
                                        }}
                                        className="mt-5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#0077b6] hover:text-[#005f92]"
                                    >
                                        {t("chat.back")}
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submitHandoff} className="space-y-3">
                                    <p className="text-xs text-neutral-600 leading-relaxed mb-1">
                                        {t("chat.handoffIntro")}
                                    </p>
                                    <input
                                        required
                                        type="email"
                                        placeholder={t("chat.handoffEmail")}
                                        value={handoff.email}
                                        onChange={(e) => setHandoff((s) => ({ ...s, email: e.target.value }))}
                                        className="w-full bg-neutral-50 border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm"
                                        data-testid="chat-handoff-email"
                                    />
                                    <input
                                        placeholder={t("chat.handoffName")}
                                        value={handoff.name}
                                        onChange={(e) => setHandoff((s) => ({ ...s, name: e.target.value }))}
                                        className="w-full bg-neutral-50 border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm"
                                        data-testid="chat-handoff-name"
                                    />
                                    <textarea
                                        rows={3}
                                        placeholder={t("chat.handoffNote")}
                                        value={handoff.note}
                                        onChange={(e) => setHandoff((s) => ({ ...s, note: e.target.value }))}
                                        className="w-full bg-neutral-50 border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm resize-none"
                                        data-testid="chat-handoff-note"
                                    />
                                    {handoff.error && (
                                        <div className="text-xs text-red-600" data-testid="chat-handoff-error">{handoff.error}</div>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={handoff.loading || !handoff.email}
                                        className="w-full bg-[#0089c8] hover:bg-[#0077b6] disabled:opacity-40 text-white font-bold uppercase tracking-[0.18em] text-xs py-3 rounded-lg transition-colors"
                                        data-testid="chat-handoff-submit"
                                    >
                                        {handoff.loading ? t("chat.handoffSending") : t("chat.handoffSubmit")}
                                    </button>
                                    <div className="text-[10px] text-neutral-400 text-center">
                                        {t("chat.handoffFooter")}
                                    </div>
                                </form>
                            )}
                        </div>
                    ) : (
                        <>
                            <div
                                ref={scrollRef}
                                className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
                                data-testid="chat-messages"
                            >
                                {messages.map((m) => (
                                    <ChatBubble key={m.id} msg={m} />
                                ))}
                                {streaming && messages.length > 0 && messages[messages.length - 1].content === "" && (
                                    <div className="flex items-center gap-2 text-neutral-400 text-xs ml-1" data-testid="chat-typing">
                                        <TypingDots />
                                    </div>
                                )}
                                {showStarters && (
                                    <div className="pt-2 space-y-2" data-testid="chat-starters">
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-400 px-1">
                                            {t("chat.startersEyebrow")}
                                        </div>
                                        {STARTER_KEYS.map((k) => (
                                            <button
                                                key={k}
                                                type="button"
                                                onClick={() => sendPrompt(t(`chat.starters.${k}.prompt`))}
                                                className="w-full text-left bg-white hover:bg-[#00aeef]/[0.08] border border-neutral-200 hover:border-[#00aeef] rounded-lg px-3 py-2.5 text-sm text-neutral-800 flex items-center justify-between gap-2 transition-colors group"
                                                data-testid={`chat-starter-${k}`}
                                            >
                                                <div>
                                                    <div className="font-semibold text-[13px]">{t(`chat.starters.${k}.label`)}</div>
                                                    <div className="text-[11px] text-neutral-500 group-hover:text-[#0077b6]">
                                                        {t(`chat.starters.${k}.hint`)}
                                                    </div>
                                                </div>
                                                <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:text-[#0077b6] flex-shrink-0" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Composer (chat mode only) */}
                            <div className="border-t border-neutral-200 p-3 flex items-end gap-2">
                                <textarea
                                    ref={inputRef}
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={onKey}
                                    rows={1}
                                    placeholder={t("chat.placeholder")}
                                    className="flex-1 resize-none bg-neutral-50 border border-neutral-200 focus:border-[#00aeef] focus:outline-none rounded-lg px-3 py-2 text-sm max-h-24"
                                    data-testid="chat-input"
                                />
                                <button
                                    type="button"
                                    onClick={send}
                                    disabled={!draft.trim() || streaming}
                                    className="w-10 h-10 rounded-lg bg-[#0089c8] hover:bg-[#0077b6] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center flex-shrink-0 transition-colors"
                                    aria-label={t("chat.send")}
                                    data-testid="chat-send"
                                >
                                    <Send className="w-4 h-4" />
                                </button>
                            </div>
                        </>
                    )}

                    {/* Disclaimer */}
                    <div className="border-t border-neutral-100 px-4 py-2 text-[9px] uppercase tracking-[0.22em] text-neutral-400 text-center bg-white">
                        {t("chat.disclaimer")}
                    </div>
                </div>
            )}
        </>
    );
}

function ChatBubble({ msg }) {
    const isUser = msg.role === "user";
    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    isUser
                        ? "bg-[#0089c8] text-white rounded-br-sm"
                        : msg.error
                        ? "bg-red-50 border border-red-200 text-red-700 rounded-bl-sm"
                        : "bg-neutral-100 text-neutral-900 rounded-bl-sm"
                }`}
            >
                {msg.content || "\u200B"}
            </div>
        </div>
    );
}

function TypingDots() {
    return (
        <div className="flex gap-1 items-end">
            <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
            <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
        </div>
    );
}
